import { useState } from "react";
import { Badge, Button, Card, Grid, Stack, Text } from "@mantine/core";
import { IconLock, IconPlayerPlay, IconSparkles } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { dynamicToolFeature, type DynamicTool, type KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { useSession } from "../lib/session";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { AiContextFields } from "../components/AiContextFields";
import { JsonView } from "../components/JsonView";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { KitchenPending } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { kitchenRequest, type MediaKind } from "../lib/kitchen";

/** Any dynamic AI tool (EasyAI Platform Routes): its inputs in, its JSON out. */
function DynamicToolForm({ tool }: { tool: DynamicTool }) {
  const kinds = tool.inputTypes.filter((k): k is MediaKind => k !== "text");
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.request<unknown>("POST", tool.path, { body: req }) });

  const run = (req: KitchenToolRequest) => {
    setLast(req);
    m.mutate(req);
  };
  const ready = Boolean(input.text.trim() || input.media);

  return (
    <>
      <PageHeader
        eyebrow={`POST ${tool.path}`}
        title={tool.summary}
        description={tool.description || "A new AI tool, published from Easy AI Studio."}
        actions={
          tool.status === "testing" ? (
            <Badge color="orange" variant="light" size="lg">
              Testing: admins only
            </Badge>
          ) : null
        }
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <MediaInput value={input} onChange={setInput} kinds={kinds} />
              <AiContextFields note={note} onNoteChange={setNote} />
              <Button leftSection={<IconSparkles size={16} />} onClick={() => run(kitchenRequest({ text: input.text, media: input.media, message: composeMessage(note) }))} loading={m.isPending} disabled={!ready} size="md">
                Run
              </Button>
              {tool.requestExample && (
                <Button variant="subtle" color="violet" size="xs" leftSection={<IconPlayerPlay size={14} />} onClick={() => run(tool.requestExample as KitchenToolRequest)} disabled={m.isPending}>
                  Run the example request
                </Button>
              )}
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && m.mutate(last)} />}
          {m.isPending && <KitchenPending title="Running…" />}
          {m.data && !m.isPending && (
            <Stack gap="md">
              <ResultHeader title="Response" meta={m.data.meta} feedback={<FeedbackButtons requestId={m.data.meta.requestId} />} />
              <JsonView value={m.data.data} expandDepth={3} fileName={`${tool.slug}.json`} />
            </Stack>
          )}
          {!m.data && !m.isPending && !m.error && <EmptyState title={tool.summary}>The response comes back as JSON, exactly as your app would get it.</EmptyState>}
        </Grid.Col>
      </Grid>
    </>
  );
}

export function DynamicToolPage() {
  const { slug = "" } = useParams();
  const { me } = useSession();
  const tool = me?.app.dynamicTools.find((t) => t.slug === slug);
  if (!tool) {
    return (
      <Card maw={560} mx="auto" mt="xl" ta="center">
        <Stack align="center" gap="sm">
          <IconLock size={32} />
          <Text fw={700} fz="lg">
            This tool isn't available right now
          </Text>
          <Text c="dimmed">It may have been switched off or not published yet. Pick another tool from the menu.</Text>
        </Stack>
      </Card>
    );
  }
  return (
    <RequireFeature feature={dynamicToolFeature(tool)}>
      <DynamicToolForm key={tool.slug} tool={tool} />
    </RequireFeature>
  );
}
